"use client";

import React, { useState } from 'react';

import Header from '@/components/Header';
import Link from 'next/link';

type SidebarItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
};

const sidebarItems: SidebarItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="2" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
        <rect x="10" y="2" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
        <rect x="2" y="10" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
        <rect x="10" y="10" width="6" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    id: 'alerts',
    label: 'Live Alerts',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M9 2a5 5 0 015 5v3l1.5 2.5H2.5L4 10V7a5 5 0 015-5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M7 14.5a2 2 0 004 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'portfolios',
    label: 'Model Portfolios',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M3 14L6 8l3 4 3-6 3 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="15" cy="6" r="1.5" fill="currentColor" />
      </svg>
    ),
  },
  {
    id: 'calendar',
    label: 'Catalyst Calendar',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <rect x="2" y="4" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
        <path d="M6 2v4M12 2v4M2 8h14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: (
      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <circle cx="9" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        <path d="M9 2v2M9 14v2M2 9h2M14 9h2M4.22 4.22l1.41 1.41M12.37 12.37l1.41 1.41M4.22 13.78l1.41-1.41M12.37 5.63l1.41-1.41" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

const alertsData = [
  { symbol: 'JNJ', type: 'BUY SIGNAL', desc: 'Dividend sustainability score crossed 9.0 threshold', time: '2 min ago', positive: true },
  { symbol: 'EUR/USD', type: 'MACRO ALERT', desc: 'ECB rate decision divergence — positioning model updated', time: '18 min ago', positive: null },
  { symbol: 'BTC', type: 'ON-CHAIN', desc: 'Whale accumulation detected — 3,200 BTC moved to cold storage', time: '45 min ago', positive: true },
  { symbol: 'VZ', type: 'RISK FLAG', desc: 'Payout ratio stress test flagged — review recommended', time: '2h ago', positive: false },
  { symbol: 'GBP/USD', type: 'SIGNAL', desc: 'Algorithmic setup triggered at 1.2580 support level', time: '3h ago', positive: true },
];

const portfolioData = [
  { name: 'Dividend Aristocrats Core', allocation: '40%', ytd: '+8.4%', yield: '3.2%', positive: true },
  { name: 'High-Yield Income', allocation: '25%', ytd: '+5.1%', yield: '5.8%', positive: true },
  { name: 'Forex Macro Alpha', allocation: '20%', ytd: '+12.7%', yield: '—', positive: true },
  { name: 'Digital Asset Satellite', allocation: '15%', ytd: '-3.2%', yield: '—', positive: false },
];

const calendarEvents = [
  { date: 'Apr 28', symbol: 'JNJ', event: 'Ex-Dividend Date', type: 'DIVIDEND' },
  { date: 'Apr 29', symbol: 'FOMC', event: 'Federal Reserve Rate Decision', type: 'MACRO' },
  { date: 'Apr 30', symbol: 'KO', event: 'Q1 Earnings Release', type: 'EARNINGS' },
  { date: 'May 2', symbol: 'EUR/USD', event: 'ECB Policy Statement', type: 'MACRO' },
  { date: 'May 5', symbol: 'PG', event: 'Ex-Dividend Date', type: 'DIVIDEND' },
];

function DonutChart({ value, color }: { value: number; color: string }) {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const dash = (value / 100) * circ;
  return (
    <svg width="90" height="90" viewBox="0 0 90 90">
      <circle cx="45" cy="45" r={r} fill="none" stroke="#1E3A5F" strokeWidth="10" />
      <circle
        cx="45" cy="45" r={r} fill="none"
        stroke={color} strokeWidth="10"
        strokeDasharray={`${dash} ${circ - dash}`}
        strokeLinecap="round"
        transform="rotate(-90 45 45)"
        style={{ transition: 'stroke-dasharray 1s ease' }}
      />
      <text x="45" y="50" textAnchor="middle" fill="#FFFFFF" fontSize="14" fontWeight="700" fontFamily="JetBrains Mono, monospace">
        {value}%
      </text>
    </svg>
  );
}

function MiniLineChart({ color }: { color: string }) {
  const points = [20, 35, 28, 45, 38, 52, 48, 60, 55, 70, 65, 78];
  const max = Math.max(...points);
  const min = Math.min(...points);
  const w = 120;
  const h = 40;
  const pts = points.map((p, i) => {
    const x = (i / (points.length - 1)) * w;
    const y = h - ((p - min) / (max - min)) * h;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function DashboardPage() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div style={{ background: '#0A2540', minHeight: '100vh' }}>
      <Header />

      <div className="flex" style={{ paddingTop: '72px', minHeight: '100vh' }}>

        {/* ── SIDEBAR ── */}
        <aside
          className={`fixed md:sticky top-0 md:top-[72px] left-0 h-full md:h-[calc(100vh-72px)] flex-shrink-0 z-30 transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
          style={{
            width: '240px',
            background: '#1A2A44',
            borderRight: '1px solid #1E3A5F',
            overflowY: 'auto',
            paddingTop: '24px',
          }}
        >
          {/* Sidebar header */}
          <div className="px-5 mb-6">
            <div className="text-xs font-mono uppercase tracking-widest mb-1"
              style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', letterSpacing: '0.18em', opacity: 0.6 }}>
              Member Portal
            </div>
            <div className="flex items-center gap-2 mt-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                style={{ background: 'linear-gradient(135deg, #4A90E2, #A3D4FF)', color: '#0A2540' }}>
                JD
              </div>
              <div>
                <div className="text-sm font-semibold text-white" style={{ fontFamily: 'var(--font-display)' }}>J. Donovan</div>
                <div className="text-xs font-mono" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)', fontSize: '10px' }}>Diamond Member</div>
              </div>
            </div>
          </div>

          <div className="h-px mx-5 mb-5" style={{ background: '#1E3A5F' }} />

          {/* Nav items */}
          <nav className="px-3">
            {sidebarItems.map((item) => (
              item.id === 'settings' ? (
                <Link
                  key={item.id}
                  href="/settings"
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl mb-1 text-sm font-medium transition-all duration-200"
                  style={{
                    background: 'transparent',
                    color: '#E3F2FD',
                    opacity: 0.55,
                    border: '1px solid transparent',
                    fontFamily: 'var(--font-body)',
                    textDecoration: 'none',
                    display: 'flex',
                  }}
                >
                  {item.icon}
                  {item.label}
                </Link>
              ) : (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl mb-1 text-sm font-medium transition-all duration-200"
                style={{
                  background: activeTab === item.id ? 'rgba(163,212,255,0.1)' : 'transparent',
                  color: activeTab === item.id ? '#A3D4FF' : '#E3F2FD',
                  opacity: activeTab === item.id ? 1 : 0.55,
                  border: activeTab === item.id ? '1px solid rgba(163,212,255,0.2)' : '1px solid transparent',
                  fontFamily: 'var(--font-body)',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                {item.icon}
                {item.label}
                {item.id === 'alerts' && (
                  <span className="ml-auto text-xs font-mono px-1.5 py-0.5 rounded"
                    style={{ background: 'rgba(248,113,113,0.15)', color: '#f87171', fontFamily: 'var(--font-mono)' }}>
                    5
                  </span>
                )}
              </button>
              )
            ))}
          </nav>

          <div className="h-px mx-5 my-5" style={{ background: '#1E3A5F' }} />

          {/* Tier badge */}
          <div className="mx-5 p-4 rounded-xl" style={{ background: 'rgba(74,144,226,0.08)', border: '1px solid rgba(74,144,226,0.2)' }}>
            <div className="text-xs font-mono mb-2" style={{ color: '#4A90E2', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em' }}>
              DIAMOND TIER
            </div>
            <div className="text-xs" style={{ color: '#E3F2FD', opacity: 0.55 }}>
              Full terminal access · API enabled
            </div>
          </div>
        </aside>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-20 md:hidden" style={{ background: 'rgba(10,37,64,0.7)' }}
            onClick={() => setSidebarOpen(false)} />
        )}

        {/* ── MAIN CONTENT ── */}
        <main className="flex-1 overflow-auto" style={{ minWidth: 0 }}>
          {/* Mobile menu toggle */}
          <div className="md:hidden flex items-center gap-3 px-6 py-4" style={{ borderBottom: '1px solid #1E3A5F' }}>
            <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M2 5h14M2 9h14M2 13h14" stroke="#A3D4FF" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
            <span className="text-sm font-semibold text-white" style={{ fontFamily: 'var(--font-display)' }}>
              {sidebarItems.find(s => s.id === activeTab)?.label}
            </span>
          </div>

          <div className="p-6 md:p-8">

            {/* ── DASHBOARD TAB ── */}
            {activeTab === 'dashboard' && (
              <div>
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <h1 className="text-2xl font-display font-bold text-white mb-1" style={{ fontFamily: 'var(--font-display)' }}>
                      Portfolio Overview
                    </h1>
                    <p className="text-sm" style={{ color: '#E3F2FD', opacity: 0.5 }}>
                      Last updated: April 25, 2025 · 09:42 EST
                    </p>
                  </div>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-mono"
                    style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.2)', color: '#4ade80', fontFamily: 'var(--font-mono)' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400" style={{ boxShadow: '0 0 6px #4ade80' }} />
                    Markets Open
                  </div>
                </div>

                {/* KPI cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  {[
                    { label: 'Portfolio Value', value: '$2.84M', delta: '+$124K', positive: true, sub: 'YTD +4.6%' },
                    { label: 'Dividend Income', value: '$8,420', delta: '+$340', positive: true, sub: 'This month' },
                    { label: 'Active Signals', value: '47', delta: '+3', positive: true, sub: 'Across all assets' },
                    { label: 'Avg. Yield', value: '3.84%', delta: '+0.12%', positive: true, sub: 'Portfolio weighted' },
                  ].map((kpi) => (
                    <div key={kpi.label} className="p-5 rounded-xl" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                      <div className="text-xs font-mono mb-3" style={{ color: '#E3F2FD', opacity: 0.45, fontFamily: 'var(--font-mono)' }}>{kpi.label}</div>
                      <div className="text-2xl font-display font-bold text-white mb-1" style={{ fontFamily: 'var(--font-display)', fontFeatureSettings: '"tnum"' }}>{kpi.value}</div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-mono" style={{ color: kpi.positive ? '#4ade80' : '#f87171', fontFamily: 'var(--font-mono)' }}>{kpi.delta}</span>
                        <span className="text-xs" style={{ color: '#E3F2FD', opacity: 0.35 }}>{kpi.sub}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Charts row */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
                  {/* Line chart placeholder */}
                  <div className="lg:col-span-2 p-6 rounded-xl" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                    <div className="flex items-center justify-between mb-5">
                      <div>
                        <div className="text-sm font-semibold text-white mb-1" style={{ fontFamily: 'var(--font-display)' }}>Portfolio Performance</div>
                        <div className="text-xs font-mono" style={{ color: '#E3F2FD', opacity: 0.4, fontFamily: 'var(--font-mono)' }}>vs. S&amp;P 500 Benchmark · YTD</div>
                      </div>
                      <div className="flex gap-4 text-xs font-mono" style={{ fontFamily: 'var(--font-mono)' }}>
                        <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 inline-block rounded" style={{ background: '#A3D4FF' }} />Portfolio</span>
                        <span className="flex items-center gap-1.5" style={{ opacity: 0.45 }}><span className="w-3 h-0.5 inline-block rounded" style={{ background: '#4A90E2' }} />Benchmark</span>
                      </div>
                    </div>
                    {/* Simulated line chart */}
                    <div className="relative" style={{ height: '160px' }}>
                      <svg width="100%" height="160" viewBox="0 0 600 160" preserveAspectRatio="none">
                        <defs>
                          <linearGradient id="lineGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#A3D4FF" stopOpacity="0.2" />
                            <stop offset="100%" stopColor="#A3D4FF" stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        {/* Area fill */}
                        <path d="M0,130 C50,120 100,110 150,95 C200,80 250,85 300,70 C350,55 400,60 450,45 C500,30 550,35 600,20 L600,160 L0,160 Z"
                          fill="url(#lineGrad)" />
                        {/* Portfolio line */}
                        <path d="M0,130 C50,120 100,110 150,95 C200,80 250,85 300,70 C350,55 400,60 450,45 C500,30 550,35 600,20"
                          fill="none" stroke="#A3D4FF" strokeWidth="2.5" strokeLinecap="round" />
                        {/* Benchmark line */}
                        <path d="M0,135 C50,130 100,125 150,115 C200,105 250,108 300,100 C350,92 400,95 450,85 C500,75 550,78 600,68"
                          fill="none" stroke="#4A90E2" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="6 4" opacity="0.5" />
                        {/* Grid lines */}
                        {[40, 80, 120].map((y) => (
                          <line key={y} x1="0" y1={y} x2="600" y2={y} stroke="#1E3A5F" strokeWidth="1" />
                        ))}
                      </svg>
                      {/* Y-axis labels */}
                      <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs font-mono pointer-events-none"
                        style={{ color: '#E3F2FD', opacity: 0.3, fontFamily: 'var(--font-mono)' }}>
                        <span>+15%</span>
                        <span>+8%</span>
                        <span>+2%</span>
                        <span>0%</span>
                      </div>
                    </div>
                  </div>

                  {/* Donut charts */}
                  <div className="p-6 rounded-xl" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                    <div className="text-sm font-semibold text-white mb-5" style={{ fontFamily: 'var(--font-display)' }}>Asset Allocation</div>
                    <div className="flex flex-col gap-5">
                      {[
                        { label: 'Dividend Equities', value: 40, color: '#A3D4FF' },
                        { label: 'Forex Strategies', value: 25, color: '#4A90E2' },
                        { label: 'Digital Assets', value: 15, color: '#60a5fa' },
                        { label: 'Cash & Equivalents', value: 20, color: '#1E3A5F' },
                      ].map((item) => (
                        <div key={item.label} className="flex items-center gap-4">
                          <div className="flex-shrink-0">
                            <DonutChart value={item.value} color={item.color} />
                          </div>
                          <div>
                            <div className="text-xs font-semibold text-white mb-0.5" style={{ fontFamily: 'var(--font-body)' }}>{item.label}</div>
                            <div className="text-xs font-mono" style={{ color: '#E3F2FD', opacity: 0.4, fontFamily: 'var(--font-mono)' }}>{item.value}% of portfolio</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Recent alerts */}
                <div className="p-6 rounded-xl" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                  <div className="flex items-center justify-between mb-5">
                    <div className="text-sm font-semibold text-white" style={{ fontFamily: 'var(--font-display)' }}>Recent Alerts</div>
                    <button onClick={() => setActiveTab('alerts')} className="text-xs font-semibold" style={{ color: '#4A90E2', background: 'none', border: 'none', cursor: 'pointer' }}>
                      View All →
                    </button>
                  </div>
                  <div className="space-y-3">
                    {alertsData.slice(0, 3).map((alert, i) => (
                      <div key={i} className="flex items-start gap-4 p-4 rounded-xl" style={{ background: '#0A2540', border: '1px solid #1E3A5F' }}>
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-xs font-bold"
                          style={{ background: 'rgba(163,212,255,0.08)', border: '1px solid rgba(163,212,255,0.15)', color: '#A3D4FF', fontFamily: 'var(--font-mono)' }}>
                          {alert.symbol.slice(0, 3)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-mono font-bold"
                              style={{ color: alert.positive === true ? '#4ade80' : alert.positive === false ? '#f87171' : '#A3D4FF', fontFamily: 'var(--font-mono)' }}>
                              {alert.type}
                            </span>
                            <span className="text-xs font-mono" style={{ color: '#E3F2FD', opacity: 0.3, fontFamily: 'var(--font-mono)' }}>{alert.time}</span>
                          </div>
                          <div className="text-sm" style={{ color: '#E3F2FD', opacity: 0.7 }}>{alert.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── LIVE ALERTS TAB ── */}
            {activeTab === 'alerts' && (
              <div>
                <div className="mb-8">
                  <h1 className="text-2xl font-display font-bold text-white mb-1" style={{ fontFamily: 'var(--font-display)' }}>Live Alerts</h1>
                  <p className="text-sm" style={{ color: '#E3F2FD', opacity: 0.5 }}>Real-time signal feed across all covered asset classes</p>
                </div>
                <div className="space-y-4">
                  {alertsData.map((alert, i) => (
                    <div key={i} className="flex items-start gap-5 p-6 rounded-xl" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-xs font-bold"
                        style={{ background: 'rgba(163,212,255,0.08)', border: '1px solid rgba(163,212,255,0.15)', color: '#A3D4FF', fontFamily: 'var(--font-mono)' }}>
                        {alert.symbol.slice(0, 3)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-xs font-mono font-bold px-2 py-0.5 rounded"
                            style={{
                              background: alert.positive === true ? 'rgba(74,222,128,0.1)' : alert.positive === false ? 'rgba(248,113,113,0.1)' : 'rgba(163,212,255,0.1)',
                              color: alert.positive === true ? '#4ade80' : alert.positive === false ? '#f87171' : '#A3D4FF',
                              fontFamily: 'var(--font-mono)',
                            }}>
                            {alert.type}
                          </span>
                          <span className="text-xs font-mono" style={{ color: '#E3F2FD', opacity: 0.35, fontFamily: 'var(--font-mono)' }}>{alert.time}</span>
                        </div>
                        <div className="text-sm text-white mb-1" style={{ fontFamily: 'var(--font-body)' }}>{alert.symbol}</div>
                        <div className="text-sm" style={{ color: '#E3F2FD', opacity: 0.65 }}>{alert.desc}</div>
                      </div>
                      <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: '#4A90E2', cursor: 'pointer', flexShrink: 0 }}>
                        View Analysis →
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── MODEL PORTFOLIOS TAB ── */}
            {activeTab === 'portfolios' && (
              <div>
                <div className="mb-8">
                  <h1 className="text-2xl font-display font-bold text-white mb-1" style={{ fontFamily: 'var(--font-display)' }}>Model Portfolios</h1>
                  <p className="text-sm" style={{ color: '#E3F2FD', opacity: 0.5 }}>Algorithmic model portfolios with real-time rebalancing signals</p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                  {portfolioData.map((p, i) => (
                    <div key={i} className="p-6 rounded-xl" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                      <div className="flex items-start justify-between mb-5">
                        <div>
                          <div className="text-base font-semibold text-white mb-1" style={{ fontFamily: 'var(--font-display)' }}>{p.name}</div>
                          <div className="text-xs font-mono" style={{ color: '#E3F2FD', opacity: 0.4, fontFamily: 'var(--font-mono)' }}>Allocation: {p.allocation}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-display font-bold mb-1" style={{ fontFamily: 'var(--font-display)', color: p.positive ? '#4ade80' : '#f87171', fontFeatureSettings: '"tnum"' }}>{p.ytd}</div>
                          <div className="text-xs font-mono" style={{ color: '#E3F2FD', opacity: 0.4, fontFamily: 'var(--font-mono)' }}>YTD Return</div>
                        </div>
                      </div>
                      <div className="mb-4">
                        <MiniLineChart color={p.positive ? '#4ade80' : '#f87171'} />
                      </div>
                      <div className="flex items-center justify-between pt-4" style={{ borderTop: '1px solid #1E3A5F' }}>
                        <div className="text-xs font-mono" style={{ color: '#E3F2FD', opacity: 0.4, fontFamily: 'var(--font-mono)' }}>
                          Avg. Yield: <span style={{ color: '#A3D4FF' }}>{p.yield}</span>
                        </div>
                        <button className="text-xs font-semibold" style={{ color: '#4A90E2', background: 'none', border: 'none', cursor: 'pointer' }}>
                          View Holdings →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── CATALYST CALENDAR TAB ── */}
            {activeTab === 'calendar' && (
              <div>
                <div className="mb-8">
                  <h1 className="text-2xl font-display font-bold text-white mb-1" style={{ fontFamily: 'var(--font-display)' }}>Catalyst Calendar</h1>
                  <p className="text-sm" style={{ color: '#E3F2FD', opacity: 0.5 }}>Upcoming ex-dividend dates, earnings, and macro events</p>
                </div>
                <div className="space-y-3">
                  {calendarEvents.map((ev, i) => (
                    <div key={i} className="flex items-center gap-5 p-5 rounded-xl" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                      <div className="flex-shrink-0 text-center w-16">
                        <div className="text-xs font-mono" style={{ color: '#E3F2FD', opacity: 0.4, fontFamily: 'var(--font-mono)' }}>{ev.date.split(' ')[0].toUpperCase()}</div>
                        <div className="text-2xl font-display font-bold text-white" style={{ fontFamily: 'var(--font-display)' }}>{ev.date.split(' ')[1]}</div>
                      </div>
                      <div className="h-10 w-px" style={{ background: '#1E3A5F' }} />
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="text-xs font-mono font-bold px-2 py-0.5 rounded"
                            style={{
                              background: ev.type === 'DIVIDEND' ? 'rgba(163,212,255,0.1)' : ev.type === 'MACRO' ? 'rgba(74,144,226,0.1)' : 'rgba(251,191,36,0.1)',
                              color: ev.type === 'DIVIDEND' ? '#A3D4FF' : ev.type === 'MACRO' ? '#4A90E2' : '#fbbf24',
                              fontFamily: 'var(--font-mono)',
                            }}>
                            {ev.type}
                          </span>
                          <span className="text-sm font-semibold" style={{ color: '#A3D4FF', fontFamily: 'var(--font-mono)' }}>{ev.symbol}</span>
                        </div>
                        <div className="text-sm text-white" style={{ fontFamily: 'var(--font-body)' }}>{ev.event}</div>
                      </div>
                      <button className="text-xs font-semibold flex-shrink-0" style={{ color: '#4A90E2', background: 'none', border: 'none', cursor: 'pointer' }}>
                        Set Alert →
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── SETTINGS TAB ── */}
            {activeTab === 'settings' && (
              <div>
                <div className="mb-8">
                  <h1 className="text-2xl font-display font-bold text-white mb-1" style={{ fontFamily: 'var(--font-display)' }}>Account Settings</h1>
                  <p className="text-sm" style={{ color: '#E3F2FD', opacity: 0.5 }}>Manage your membership, notifications, and data preferences</p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {[
                    { title: 'Membership Plan', desc: 'Diamond Tier · Renews May 1, 2025', action: 'Manage Plan' },
                    { title: 'Alert Preferences', desc: 'Email + In-app · All asset classes enabled', action: 'Configure' },
                    { title: 'API Access', desc: 'API key active · 2,840 calls this month', action: 'View Keys' },
                    { title: 'Data Exports', desc: 'CSV, JSON, Excel formats available', action: 'Export Data' },
                  ].map((setting) => (
                    <div key={setting.title} className="flex items-center justify-between p-6 rounded-xl" style={{ background: '#1A2A44', border: '1px solid #1E3A5F' }}>
                      <div>
                        <div className="text-sm font-semibold text-white mb-1" style={{ fontFamily: 'var(--font-display)' }}>{setting.title}</div>
                        <div className="text-xs" style={{ color: '#E3F2FD', opacity: 0.5 }}>{setting.desc}</div>
                      </div>
                      <button className="text-xs font-semibold px-4 py-2 rounded-lg ml-4 flex-shrink-0"
                        style={{ background: 'rgba(163,212,255,0.08)', border: '1px solid rgba(163,212,255,0.2)', color: '#A3D4FF', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                        {setting.action}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </main>
      </div>
    </div>
  );
}
